import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_INTERFACES, hasPermission } from '../shared/adminInterfaces';
import { Icon, Notice } from '../exchanges/components';
import Login from '../exchanges/Login';
import PlatformHeader from '../exchanges/PlatformHeader';
import SecurityDialog from '../exchanges/SecurityDialog';
import { portalRequest } from './api';
import SalesforceMapping from './SalesforceMapping';
import AdminMail from '../AdminMail';
import AccessManagement from './AccessManagement';
import '../exchanges/exchanges.css';
import './admin.css';

const SECTION = { label: 'Portail d’administration', icon: 'home' };

const LOGIN_COPY = {
  section: SECTION,
  storyEyebrow: 'ESPACE ADMINISTRATIF',
  storyTitle: 'Portail d’administration des inscriptions',
  storyText: 'Accédez aux interfaces d’administration qui vous sont attribuées : étudiants en échange, correspondances Salesforce et gestion des accès.',
  storyFooter: 'ICES · Plateforme d’inscription',
  storyIcon: 'shield',
  cardEyebrow: 'PORTAIL D’ADMINISTRATION'
};

/**
 * Point d'entrée des interfaces d'administration.
 * view : 'home' (choix de l'interface), 'salesforce' ou 'access'.
 * L'identification (mot de passe + clé d'accès) est celle des échanges.
 */
export default function AdminPortal({ view = 'home' }) {
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    document.body.classList.add('exchange-admin-page');
    return () => document.body.classList.remove('exchange-admin-page');
  }, []);
  const loadAuth = useCallback(async () => { const data = await portalRequest('/auth/me'); setAuth(data); setError(''); }, []);
  const expired = useCallback(() => { setAuth(null); setError('Votre session a expiré. Veuillez vous reconnecter.'); }, []);
  useEffect(() => {
    let active = true;
    portalRequest('/auth/me').then((data) => { if (active) setAuth(data); })
      .catch((e) => { if (active && e.status !== 401) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  return <div className="exchange-admin ad-portal">{loading ? <div className="ex-loading" role="status">Chargement du portail…</div>
    : auth ? <Shell auth={auth} view={view} onExpired={expired} onLogout={() => { setAuth(null); setError(''); }} />
      : <Login onAuthenticated={loadAuth} initialError={error} request={portalRequest} copy={LOGIN_COPY} />}</div>;
}

function Shell({ auth, view, onExpired, onLogout }) {
  const api = useCallback(async (path, options = {}) => {
    try { return await portalRequest(path, { csrf: auth.csrf, ...options }); }
    catch (error) { if (error.status === 401) onExpired(); throw error; }
  }, [auth.csrf, onExpired]);
  const [security, setSecurity] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const permissions = auth.permissions || [];
  const current = ADMIN_INTERFACES.find((item) => item.key === view);

  async function logout() {
    setBusy(true);
    try { await api('/auth/logout', { method: 'POST', body: {} }); onLogout(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  let content;
  if (view === 'home') content = <Home auth={auth} permissions={permissions} />;
  else if (!current || !hasPermission(permissions, current.key)) content = <NoAccess name={current?.label} />;
  else if (view === 'mail') content = <AdminMail api={api} />;
  else if (view === 'salesforce') content = <SalesforceMapping api={api} permissions={permissions} />;
  else if (view === 'access') content = <AccessManagement api={api} auth={auth} />;

  return <>
    <PlatformHeader section={current ? { label: current.label, icon: current.icon } : SECTION}>
      <div className="ex-account">
        <span className="ex-avatar">{auth.user.name.charAt(0)}</span>
        <span className="ex-account-name">{auth.user.name}<small>Portail d’administration</small></span>
        {view !== 'home' && <Link className="ex-icon-button" to="/admin" aria-label="Retour au portail" title="Retour au portail"><Icon name="home" /></Link>}
        <button className="ex-icon-button" onClick={() => setSecurity(true)} aria-label="Gérer mes clés d’accès" title="Mes clés d’accès"><Icon name="key" /></button>
        <button className="ex-icon-button" disabled={busy} onClick={logout} aria-label="Se déconnecter" title="Se déconnecter"><Icon name="logout" /></button>
      </div>
    </PlatformHeader>
    <main className="ex-workspace">
      <Notice>{error}</Notice>
      {content}
      <p className="ex-workspace-footer">ICES · Plateforme d’inscription<span>Portail d’administration</span></p>
    </main>
    {security && <SecurityDialog api={api} onClose={() => setSecurity(false)} />}
  </>;
}

function Home({ auth, permissions }) {
  const available = ADMIN_INTERFACES.filter((item) => hasPermission(permissions, item.key));
  // Page historique : son authentification par jeton admin reste inchangée.
  // Ce raccourci facilite l'accès sans lui attribuer un nouveau droit de session.
  if (permissions.includes('access')) available.unshift({
    key: 'dossiers', label: 'Dossiers',
    description: 'Consulter les dossiers étudiants, leurs pièces et créer les liens responsables de candidature.',
    path: '/admin/dossiers', icon: 'folder'
  });
  return <>
    <div className="ex-page-heading"><div><p className="ex-eyebrow">PORTAIL D’ADMINISTRATION</p><h1>Bonjour {auth.user.name}</h1><p className="ex-muted">Choisissez l’interface d’administration à ouvrir.</p></div></div>
    {available.length ? <div className="ad-tiles">{available.map((item) => {
      const inner = <><span className="ex-feature-icon"><Icon name={item.icon} size={26} /></span><span className="ad-tile-text"><strong>{item.label}</strong><small>{item.description}</small></span><Icon name="arrow" /></>;
      // Les échanges et les dossiers historiques sont des pages distinctes.
      return ['exchanges', 'dossiers'].includes(item.key)
        ? <a key={item.key} className="ad-tile" href={item.path}>{inner}</a>
        : <Link key={item.key} className="ad-tile" to={item.path}>{inner}</Link>;
    })}</div>
      : <div className="ex-empty"><div className="ex-feature-icon"><Icon name="lock" size={30} /></div><h3>Aucune interface ne vous est attribuée</h3><p>Demandez à un administrateur de vous donner accès aux interfaces dont vous avez besoin.</p></div>}
  </>;
}

function NoAccess({ name }) {
  return <div className="ex-empty"><div className="ex-feature-icon"><Icon name="lock" size={30} /></div>
    <h3>Accès non attribué</h3>
    <p>Votre compte n’a pas accès {name ? `à l’interface « ${name} »` : 'à cette interface'}. Demandez cet accès à un administrateur.</p>
    <Link className="ex-button ex-button--primary" to="/admin"><Icon name="home" size={17} />Retour au portail</Link>
  </div>;
}
